import React, { useState } from "react";
import { X, Star } from "lucide-react";

const CATEGORY_SUGGESTIONS = [
  "Kitchen", "Bathroom", "Yard", "Living Room", "Bedroom",
  "Laundry", "Trash", "Dishes", "Shopping", "Pets", "Other",
];

const ChoreModal = ({ onClose, onSave, onDelete, chore, members }) => {
  const [title, setTitle] = useState(chore?.title || "");
  const [description, setDescription] = useState(chore?.description || "");
  const [category, setCategory] = useState(chore?.category || "");
  const [dueDate, setDueDate] = useState(chore?.due_date || "");
  const [points, setPoints] = useState(chore?.points ?? 1);
  const [assignedMemberIds, setAssignedMemberIds] = useState(
    chore?.assigned_member_ids || []
  );
  const [repeat, setRepeat] = useState(chore?.repeat || "");

  const toggleMember = (id) => {
    setAssignedMemberIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    onSave({
      title: title.trim(),
      description: description.trim() || null,
      category: category.trim() || null,
      due_date: dueDate || null,
      points,
      assigned_member_ids: assignedMemberIds.length > 0 ? assignedMemberIds : null,
      repeat: repeat || null,
    });
  };

  const handleDelete = () => {
    if (window.confirm("Delete this chore?")) onDelete(chore.id);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
      <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-lg p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">
            {chore ? "Edit Chore" : "New Chore"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-2 touch-manipulation"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="e.g. Vacuum living room"
              required
              autoFocus
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <input
              list="chore-categories"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="e.g. Kitchen, Yard, Bathroom"
            />
            <datalist id="chore-categories">
              {CATEGORY_SUGGESTIONS.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Due Date
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Repeat */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Repeat
            </label>
            <div className="flex gap-2 flex-wrap">
              {[
                { value: "", label: "None" },
                { value: "daily", label: "Daily" },
                { value: "weekly", label: "Weekly" },
                { value: "monthly", label: "Monthly" },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setRepeat(value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border-2 transition-colors touch-manipulation ${
                    repeat === value
                      ? "border-purple-500 bg-purple-50 text-purple-700"
                      : "border-gray-200 text-gray-500 hover:border-gray-300"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {repeat && (
              <p className="text-xs text-gray-400 mt-1.5">
                Completing this chore will advance the due date to the next occurrence.
              </p>
            )}
          </div>

          {/* Points */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Points
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPoints(n)}
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium border-2 transition-colors touch-manipulation ${
                    points === n
                      ? "border-yellow-400 bg-yellow-50 text-yellow-700"
                      : "border-gray-200 text-gray-500 hover:border-yellow-300"
                  }`}
                >
                  <Star
                    size={14}
                    className={
                      points >= n
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-gray-300"
                    }
                  />
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* Assign to Members */}
          {members.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assign To
              </label>
              <div className="flex flex-wrap gap-2">
                {members.map((member) => {
                  const selected = assignedMemberIds.includes(member.id);
                  return (
                    <button
                      key={member.id}
                      type="button"
                      onClick={() => toggleMember(member.id)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-colors touch-manipulation ${
                        selected
                          ? "text-white border-transparent"
                          : "border-gray-200 text-gray-700 bg-white hover:border-gray-300"
                      }`}
                      style={
                        selected
                          ? { backgroundColor: member.color, borderColor: member.color }
                          : {}
                      }
                    >
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: member.color }}
                      />
                      {member.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              rows={2}
              placeholder="Optional notes..."
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            {chore && (
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 touch-manipulation"
              >
                Delete
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 touch-manipulation"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 touch-manipulation"
            >
              {chore ? "Save" : "Add Chore"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChoreModal;
